import { mapModeWantsCrosshair, type MapMode } from '@/lib/map-mode';
import maplibregl from 'maplibre-gl';
import { STATUS_COLORS, type MapStyleKey } from './droneMapTypes';
import { DOCK_STATE_COLORS, type DockSymbolState } from './droneMapGeoJson';

/**
 * Radius (px) of the invisible `dock-hit` circle that captures dock clicks.
 * Deliberately generous so a dock is easy to hit at any zoom. The click is
 * bound to this layer - never to the visible marker - so changing the marker
 * (e.g. the T1.2 square glyph) can never silently break the click again.
 */
export const DOCK_HIT_RADIUS_PX = 22;
/** Smaller than the dock's, so a trigger sitting on a dock does not swallow every
 *  click meant for the dock underneath it. */
export const TRIGGER_HIT_RADIUS_PX = 16;

const ENGLISH_LABEL_EXPR: maplibregl.ExpressionSpecification = [
  'coalesce',
  ['get', 'name:en'],
  ['get', 'name_en'],
  ['get', 'name:latin'],
  ['get', 'name'],
];

const NAME_TOKEN = /name/;

function textFieldReferencesName(textField: unknown): boolean {
  if (typeof textField === 'string') return NAME_TOKEN.test(textField);
  if (!Array.isArray(textField)) return false;
  for (const part of textField) {
    if (typeof part === 'string' && NAME_TOKEN.test(part)) return true;
    if (Array.isArray(part) && textFieldReferencesName(part)) return true;
  }
  return false;
}

function makeTfrHatchImageData(): ImageData {
  const size = 32;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(220, 38, 38, 0.75)';
  ctx.lineWidth = 2;
  for (let x = -size; x < size * 2; x += 8) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + size, size);
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(180, 20, 20, 0.45)';
  for (let x = -size; x < size * 2; x += 8) {
    ctx.beginPath();
    ctx.moveTo(x + size, 0);
    ctx.lineTo(x, size);
    ctx.stroke();
  }
  return ctx.getImageData(0, 0, size, size);
}

function drawRoundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const DOCK_GLYPH_SIZE = 48;

/**
 * Top-down FlytMini docking-station glyph, drawn on a canvas so it needs no
 * network asset (a blocked CDN can never blank it). A rounded ALUMINIUM SQUARE
 * - unmistakably not a drone circle - with a status-coloured frame, a central
 * cone landing hub (ring + centre mark), and four battery/motor bays at the
 * corners. `color` is the live status colour, so each of the four status
 * variants reads at a glance.
 */
function makeDockGlyphImageData(color: string): ImageData {
  const c = document.createElement('canvas');
  c.width = DOCK_GLYPH_SIZE;
  c.height = DOCK_GLYPH_SIZE;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.clearRect(0, 0, DOCK_GLYPH_SIZE, DOCK_GLYPH_SIZE);

  // Dock body: rounded square, dark fill, status-coloured frame.
  drawRoundedRectPath(ctx, 5, 5, 38, 38, 9);
  ctx.fillStyle = 'rgba(9, 9, 11, 0.92)';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = color;
  ctx.lineJoin = 'round';
  ctx.stroke();

  // Cone landing hub (top-down = ring) + centre mark.
  ctx.beginPath();
  ctx.arc(24, 24, 9, 0, Math.PI * 2);
  ctx.lineWidth = 3;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(24, 24, 2.6, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  // Four battery/motor bays at the corners.
  for (const [cx, cy] of [[12, 12], [36, 12], [12, 36], [36, 36]] as const) {
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();
  }

  return ctx.getImageData(0, 0, DOCK_GLYPH_SIZE, DOCK_GLYPH_SIZE);
}

/** Register the four dock status glyphs once (idempotent across style reloads). */
function ensureDockGlyphs(map: maplibregl.Map) {
  for (const [state, color] of Object.entries(DOCK_STATE_COLORS) as [DockSymbolState, string][]) {
    const imageId = `dock-${state}`;
    try {
      // Unconditional remove-then-add, matching drone-icon/rc-icon/
      // traffic-diamond-icon elsewhere in this file - NOT a hasImage() skip.
      // map.setStyle() (the dark<->bright toggle) replaces the style's
      // whole image sprite, but hasImage() was observed returning a STALE
      // true for these canvas-registered images across that swap, so the
      // skip-if-exists guard silently left the new style with no image at
      // all - the dock glyph disappeared entirely after toggling style
      // (found via T3.1's map-style-toggle test).
      if (map.hasImage(imageId)) map.removeImage(imageId);
      map.addImage(imageId, makeDockGlyphImageData(color), { pixelRatio: 2 });
    } catch {
      // Canvas unavailable - the label + hit target still identify the dock.
    }
  }
}

/**
 * Selection-bracket geometry, in CSS px. Reviewed and signed off against a live
 * mock - do not drift these without re-checking how they read at map scale.
 */
const BRACKET_HALF_EXTENT_PX = 23; // centre -> corner, i.e. half the bracket box
const BRACKET_CORNER_PX = 12; // length of each arm of a corner
const BRACKET_STROKE_PX = 3;
const BRACKET_COLOR = '#FFFFFF';
/**
 * Dark casing drawn under the white stroke, for the same reason every other
 * white mark in this file carries a black halo (drone-labels, dock-labels,
 * site-labels): the basemap toggles between a dark style and Carto Voyager, and
 * bare white over pale roads is the weakest mark on the display. Casing keeps
 * the one white-means-selected rule working in both themes without a per-theme
 * asset.
 */
const BRACKET_CASING_PX = 5;
const BRACKET_CASING_COLOR = 'rgba(0, 0, 0, 0.75)';
/** Canvas padding so the widest stroke is never clipped at the image edge. */
const BRACKET_PAD_PX = BRACKET_CASING_PX / 2 + 1;
export const BRACKET_IMAGE_ID = 'selection-bracket';

/**
 * Four corner brackets marking the chosen object - the "[ ]" the operator sees
 * around a selected drone or dock.
 *
 * Corners only, never a closed box: a full rectangle hides the marker's own
 * edge and reads as a geofence. Drawn on a canvas for the same reason as the
 * dock glyph - no network asset, so a blocked CDN can never blank it.
 *
 * Registered NON-SDF (unlike drone-icon / traffic-diamond-icon nearby). SDF
 * treats the alpha channel as a distance field, which softens a 3px stroke into
 * a blur at map scale; the bracket is thin enough that crispness matters more
 * than the tint-at-runtime that SDF would buy. The colour is decided (white, so
 * selection never collides with a red critical marker's status colour), so
 * recolouring means changing BRACKET_COLOR here rather than a paint property.
 */
function makeSelectionBracketImageData(): ImageData {
  const scale = 2; // matches the pixelRatio: 2 passed to addImage
  const half = BRACKET_HALF_EXTENT_PX + BRACKET_PAD_PX;
  const size = Math.ceil(half * 2 * scale);
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.clearRect(0, 0, size, size);

  const mid = size / 2;
  const ext = BRACKET_HALF_EXTENT_PX * scale;
  const arm = BRACKET_CORNER_PX * scale;

  ctx.lineCap = 'square';
  ctx.lineJoin = 'miter';

  // [x, y, dx, dy] per corner: dx/dy point back toward the centre.
  const corners: [number, number, number, number][] = [
    [mid - ext, mid - ext, 1, 1],
    [mid + ext, mid - ext, -1, 1],
    [mid - ext, mid + ext, 1, -1],
    [mid + ext, mid + ext, -1, -1],
  ];
  const strokeCorners = () => {
    for (const [x, y, dx, dy] of corners) {
      ctx.beginPath();
      ctx.moveTo(x + dx * arm, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y + dy * arm);
      ctx.stroke();
    }
  };

  // Casing first, white on top - the white must stay the dominant mark.
  ctx.strokeStyle = BRACKET_CASING_COLOR;
  ctx.lineWidth = BRACKET_CASING_PX * scale;
  strokeCorners();
  ctx.strokeStyle = BRACKET_COLOR;
  ctx.lineWidth = BRACKET_STROKE_PX * scale;
  strokeCorners();

  return ctx.getImageData(0, 0, size, size);
}

/** Register the selection bracket once (idempotent across style reloads). */
export function ensureSelectionBracketImage(map: maplibregl.Map) {
  try {
    // Draw BEFORE removing: if the canvas throws, the previously-registered
    // image must survive. Removing first and failing to rebuild would leave
    // both bracket layers pointing at a missing sprite entry for the rest of
    // the session, with a per-frame MapLibre warning and no bracket.
    const image = makeSelectionBracketImageData();
    // Unconditional remove-then-add, for the same reason spelled out in
    // ensureDockGlyphs: map.setStyle() replaces the style's whole image sprite
    // and hasImage() was observed returning a STALE true across that swap, so a
    // skip-if-exists guard silently leaves the new style with no image at all.
    if (map.hasImage(BRACKET_IMAGE_ID)) map.removeImage(BRACKET_IMAGE_ID);
    map.addImage(BRACKET_IMAGE_ID, image, { pixelRatio: 2 });
  } catch (err) {
    // Canvas unavailable - markers still render, selection is just not drawn.
    // Logged rather than swallowed: a silently missing selection indicator is
    // undiagnosable from the operator's seat.
    console.warn('[map] selection bracket unavailable, selection will not be drawn', err);
  }
}

/**
 * Layout shared by both selection-bracket layers.
 *
 * `icon-rotation-alignment: 'viewport'` and the absence of `icon-rotate` are
 * load-bearing: the drone marker turns with its heading, but a selection
 * bracket that spins with the aircraft reads as a malfunction. The bracket
 * stays square to the screen.
 *
 * allow-overlap + ignore-placement so symbol collision can never drop the
 * bracket - the one marker the operator explicitly asked for must always draw.
 */
export const SELECTION_BRACKET_LAYOUT: maplibregl.SymbolLayerSpecification['layout'] = {
  'icon-image': BRACKET_IMAGE_ID,
  'icon-rotation-alignment': 'viewport',
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
};

/** Show the bracket only on the feature the operator picked. */
export const SELECTION_BRACKET_FILTER: maplibregl.FilterSpecification = ['==', ['get', 'selected'], true];

export const SELECTION_BRACKET_LAYER_IDS = ['dock-selection-bracket', 'drone-selection-bracket'] as const;

/**
 * A human has their hands on this aircraft.
 *
 * Amber — the warning hue, and the one the MANUAL refusal already speaks in.
 * For everybody who is NOT flying it this is a caution and not a fault: the
 * aircraft is healthy, but a GO-TO will be refused and an RTH will pull it out
 * of somebody's hand. Reusing STATUS_COLORS.warning rather than a literal so
 * the mark can never drift from the palette the rest of the picture uses.
 */
export const MANUAL_HOLD_COLOR = STATUS_COLORS.warning;

/** Paint the manual mark only on aircraft a live hold was resolved for. */
export const MANUAL_HOLD_FILTER: maplibregl.FilterSpecification = ['==', ['get', 'manual_hold'], true];

export const MANUAL_HOLD_LAYER_IDS = ['drone-manual-ring', 'drone-manual-tag'] as const;

/**
 * Callsign offset (in ems) that clears the bracket's bottom arms.
 *
 * The arms sit at BRACKET_HALF_EXTENT_PX (23) + half the casing, i.e. ~25.5px
 * below centre, spanning the same x range a 5-7 character callsign occupies.
 * The unselected offsets put the label at 22px (drone, 10px text) and 19.8px
 * (dock, 9px text) - straight through the arms. Nudging the label of the
 * SELECTED feature only, so nothing moves for the rest of the fleet.
 */
export function selectedTextOffset(
  baseEm: number,
  textSizePx: number,
  /**
   * Which side of the marker the label sits on. 'above' is the same clearance
   * arithmetic mirrored: the bracket is square, so its top arms are exactly as
   * far from centre as its bottom ones, and a tag placed above a selected
   * aircraft is struck through by them at precisely the offsets that work
   * below.
   */
  place: 'below' | 'above' = 'below',
): maplibregl.ExpressionSpecification {
  const clearedEm = (BRACKET_HALF_EXTENT_PX + BRACKET_CASING_PX / 2 + 7) / textSizePx;
  const sign = place === 'above' ? -1 : 1;
  return [
    'case',
    ['get', 'selected'],
    ['literal', [0, Number((sign * clearedEm).toFixed(2))]],
    ['literal', [0, sign * baseEm]],
  ] as unknown as maplibregl.ExpressionSpecification;
}

/**
 * Re-assert both brackets as the topmost layers.
 *
 * Three marker icons (rc-icon, traffic-diamond, drone-icon) are added from
 * independent `img.onload` callbacks with no `beforeId`, so each one appends to
 * the top of the stack in whatever order the browser happens to resolve them -
 * an order that differs between a cold load and a warm dark<->bright toggle.
 * Raising the brackets from just one of those callbacks left the outcome to
 * that race, so a controller or traffic glyph could land on top of the bracket
 * for some sessions and not others. Calling this at the end of every path that
 * adds a layer makes the result deterministic instead.
 *
 * The dock bracket is raised first so the drone bracket ends up above it: when
 * a drone sits on the selected dock, the aircraft's own bracket wins.
 */
/** The handover countdown's layer, which rides this raise - see `addHandoverLinkLayers`. */
export const HANDOVER_LINK_LABEL_LAYER_ID = 'handover-link-label';

export function raiseSelectionBrackets(map: maplibregl.Map) {
  // The handover countdown first: over every marker and callsign, still under the brackets. Two
  // aircraft stacked over one station is exactly when a label beneath them would be lost.
  if (map.getLayer(HANDOVER_LINK_LABEL_LAYER_ID)) map.moveLayer(HANDOVER_LINK_LABEL_LAYER_ID);
  // Then the MANUAL tag back over it (second review round). The countdown sits above the stacked
  // pair in the same band as the tag, and a hand-flown aircraft's mark is the one that must not be
  // covered - its own comment says a mark that vanishes as aircraft close up is worse than none.
  if (map.getLayer('drone-manual-tag')) map.moveLayer('drone-manual-tag');
  for (const id of SELECTION_BRACKET_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

const AIRSPACE_LAYER_IDS = [
  'airspace-e-fill',
  'airspace-b-fill',
  'airspace-b-outline',
  'airspace-c-fill',
  'airspace-c-outline',
  'airspace-d-fill',
  'airspace-d-outline',
  'airspace-class-labels',
  'airspace-tfr-fill',
  'airspace-tfr-outline',
  'airspace-tfr-labels',
  'airspace-notam-symbols',
] as const;

export function setAirspaceLayerVisibility(map: maplibregl.Map, visible: boolean) {
  const vis = visible ? 'visible' : 'none';
  for (const id of AIRSPACE_LAYER_IDS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
  }
}

/** Width (px) of the invisible `mission-route-hit` line that captures route
 *  clicks. A 2px dashed polyline is a hopeless click target at operational
 *  zooms; same reasoning as `dock-hit` above — clicks bind to this layer,
 *  never the visible line, so restyling the route cannot break selection. */
export const MISSION_ROUTE_HIT_WIDTH_PX = 14;

/**
 * Radius (px) of the invisible `plan-waypoint-hit` circle that captures every
 * gesture on a draft-route waypoint: grab-to-drag, click-to-select, and the
 * "did this click land on an existing waypoint?" test that decides whether a
 * click ADDS a point. Same discipline as `dock-hit` and `mission-route-hit` —
 * the gesture binds to this layer, never to the visible dot, so restyling the
 * dot can never silently shrink the target again.
 *
 * The visible dot is 8px (10 selected, 11 active) and hit-testing used to run
 * against exactly that geometry. Measured on dev: a drag starting ~22px off a
 * waypoint's centre matched no layer at all, so MapLibre's drag-pan took the
 * gesture and the MAP slid instead of the waypoint. Operators reported that as
 * "I can't drag waypoints, I can only add them".
 *
 * ONE radius serves both "grab it" and "don't add on top of it", deliberately.
 * A grab radius larger than the add-suppression radius would leave a ring of
 * pixels where a drag moves waypoint N but a plain click appends N+1 — the
 * same pixel meaning two things depending on whether the mouse happened to
 * move, which is the exact class of error this map's single-mode arbitration
 * exists to prevent. The cost is that two waypoints cannot be placed within
 * 18px of each other without zooming in; that is the right way to lose,
 * because a suppressed add is VISIBLE (the near waypoint gets selected
 * instead, so the operator zooms and retries) while a stray add silently
 * corrupts the route being edited.
 */
export const PLAN_WAYPOINT_HIT_RADIUS_PX = 18;

/** Zoom below which the range rings collapse into one smudge, and the zoom below
 *  which their captions are more ink than information. */
export const RANGE_RING_MIN_ZOOM = 9;
export const RANGE_RING_LABEL_MIN_ZOOM = 11;

/**
 * Ring stroke width, in px.
 *
 * Was 1. A 1px arc is antialiased across two subpixels, so its brightest pixel
 * only ever reaches ~68% of the declared opacity and its neighbours much less —
 * the ring is thinner than one pixel of ink almost everywhere. 1.5 gives the
 * curve at least one fully-covered pixel to land on, which is what makes the
 * arc resolve as a line instead of a rumour. `line-dasharray` is measured in
 * line widths, so the dash rhythm scales with it and stays proportionate.
 */
const RANGE_RING_WIDTH_PX = 1.5;

/**
 * The ring's ink, per basemap.
 *
 * Manual QA on dev, with an airborne aircraft picked and the overlay on: "I see
 * labels with the 1, 2, 5 km but no rings." A pixel diff of the COP with the
 * overlay off vs on proved the arcs WERE being drawn — the rings are not a
 * rendering bug, they were below the threshold of perception. Measured on the
 * dark basemap, the brightest pixel of a ring was RGB(42,42,42) against an
 * RGB(12,12,12) background: a contrast ratio of 1.36:1, and 1.16:1 at the
 * median. WCAG 1.4.11 asks 3:1 of a non-text graphic that carries meaning, and
 * an operations display should not be arguing for less. Next to a haloed 75%
 * caption, the caption read and the ring did not — exactly the report.
 *
 * The design intent the rings were given is kept, not traded away: they are
 * still dashed, still one thin stroke, still the neutral register (white on
 * dark) that the selection bracket owns, and they are still registered at the
 * bottom of the stack so every track draws over them. Only the ink is raised to
 * where an eye can pick it up, to ~4:1 on dark.
 *
 * Raised a second time after the founder read the first pass on dev: the rings
 * were legible but still read as a hint rather than a ring, so the ink went to
 * 0.65 on dark (~8.4:1) and 0.70 on bright. The register is unchanged - same
 * neutral, same dash, same thin stroke, still bottom-of-stack. What bounds this
 * is not a fixed number but the layer it must not compete with: a mission route
 * draws at 0.8 when it is neither selected nor hovered, and the rings stay under
 * that, which is the constraint the test now reads out of the route's own paint
 * rather than restating as a constant.
 *
 * White is the neutral only on the dark basemap. On 'bright' (CARTO voyager,
 * a near-white land fill) white rings are invisible at any opacity that is not
 * garish, so the neutral inverts to the palette's own near-black — the same
 * swap the RC icon already makes between styles. Opacity differs because the
 * two backgrounds are not symmetric: dark ink on a light ground loses more
 * contrast per unit alpha than light ink on a dark one.
 */
const RANGE_RING_INK: Record<MapStyleKey, { color: string; opacity: number }> = {
  dark: { color: '#FFFFFF', opacity: 0.65 },
  bright: { color: '#18181B', opacity: 0.7 },
  // Imagery is neither of the above. It is not a flat fill at all: one pan can
  // cross dark vegetation, mid-tone roof and near-white concrete, so no single
  // ink wins on contrast alone. White at a higher alpha than 'dark' is the
  // cartographic convention over imagery - it holds on the dark two-thirds and
  // stays visible on the bright third because the ring is dashed and thin, so it
  // reads as a line rather than needing to out-contrast the ground.
  satellite: { color: '#FFFFFF', opacity: 0.8 },
};

/** Ring caption ink, per basemap. A black halo under a gray glyph is legible on
 *  the dark style and turns the caption into an outlined smudge on the bright
 *  one, so the halo inverts with the ground it sits on. */
const RANGE_RING_LABEL_INK: Record<MapStyleKey, { color: string; halo: string }> = {
  dark: { color: '#A1A1AA', halo: '#000000' },
  bright: { color: '#3F3F46', halo: '#FFFFFF' },
  // Over imagery the halo does the work, not the fill. The gray used on 'dark'
  // disappears against roof and road at the same time, so the caption goes to
  // pure white with a black halo - the pairing every aeronautical chart uses
  // over photography, and the only one that survives a ground it cannot predict.
  satellite: { color: '#FFFFFF', halo: '#000000' },
};

/**
 * How much of the track ink survives the ground under it.
 *
 * The trails, the controller link and the geofence wash were all tuned against
 * two FLAT basemaps, where a 30-50% hairline reads cleanly. Imagery is neither
 * flat nor predictable: one pan crosses dark vegetation, mid-grey asphalt and
 * near-white concrete, and a translucent line disappears over at least one of
 * them. Since imagery became the default ground this is what most operators see,
 * so the ink goes opaque and a little wider over it.
 *
 * This is the cheap half of the fix and it is deliberately the only half here.
 * The T3 plan specified a black CASING under each line, which is the treatment
 * that actually guarantees contrast over an unpredictable ground - that needs a
 * second layer per line and, more to the point, needs judging on a screen over a
 * real site. Tracked rather than guessed at blind.
 */
const TRACK_INK: Record<MapStyleKey, { autoTrail: number; trail: number; link: number; geofenceFill: number; width: number }> = {
  dark:      { autoTrail: 0.3, trail: 0.5, link: 0.6, geofenceFill: 0.1,  width: 1 },
  bright:    { autoTrail: 0.3, trail: 0.5, link: 0.6, geofenceFill: 0.1,  width: 1 },
  satellite: { autoTrail: 0.7, trail: 0.9, link: 0.9, geofenceFill: 0.22, width: 1.5 },
};

/** The imagery layer's id in the style this API serves (`src/api/basemap.ts`). */
export const IMAGERY_LAYER_ID = 'satellite';

/**
 * How bright the imagery is allowed to be, per display mode.
 *
 * NIGHT exists so an operator keeps their dark adaptation on a night shift. The
 * COP is a full-bleed map with the chrome floating over it, so once imagery
 * became the default ground, pressing NIGHT re-tinted a few small panels and
 * left daylight photography across most of the screen - the control stopped
 * doing the one thing it is for. Dimming the ground is NOT the old D3 coupling
 * coming back: the mode does not choose WHICH basemap is drawn, only how bright
 * the drawn one is allowed to be.
 *
 * 0.75 is the figure the T3 plan proposed and NOBODY HAS JUDGED IT ON A SCREEN.
 * It is a starting point to tune against a real site at night, not a measurement.
 */
export const IMAGERY_BRIGHTNESS_MAX: Record<'light' | 'dark', number> = { light: 1, dark: 0.75 };

/**
 * Dim the imagery for NIGHT, or restore it for DAY. A no-op on the vector
 * basemaps, which have no raster layer and are already drawn per mode.
 */
export function applyImageryBrightness(map: maplibregl.Map, theme: 'light' | 'dark') {
  if (!map.getLayer(IMAGERY_LAYER_ID)) return;
  map.setPaintProperty(IMAGERY_LAYER_ID, 'raster-brightness-max', IMAGERY_BRIGHTNESS_MAX[theme]);
}

/**
 * EVERY layer the missions toggle governs. A new mission layer that is not in this list is
 * created with the right filter once and then desynchronises for the rest of the session
 * (#700 review HIGH).
 *
 * That is not theoretical: `showMissions` defaults to FALSE, so these are created filtered to
 * `in_progress`, and `setMissionLayerVisibility` is the only thing that ever clears the filter.
 * The orbit rings were added inside `addMissionRouteLayers` on the reasoning that they would
 * "inherit" its filter - true at creation, and false from the first toggle onward. A scheduled
 * orbit then rendered as a bare centre dot again, which is the exact defect the rings were added
 * to fix; and the mirror case left rings drawn over a picture whose routes had just been
 * narrowed.
 *
 * The loop below skips ids that are not on the map, so listing a layer the static
 * `MissionRouteMap` never creates costs nothing.
 */
const MISSION_LAYER_IDS = [
  'mission-route-lines',
  'mission-route-hit',
  'mission-orbit-fill',
  'mission-orbit-line',
  'mission-waypoint-circles',
  'mission-waypoint-labels',
] as const;

/**
 * A mission being FLOWN is never hidden by the mission-routes toggle.
 *
 * The toggle means "do not paint PLANNED routes on the resting picture", which
 * is why it defaults off. It cannot mean "hide where the airborne aircraft is
 * going": an operator presses LAUNCH, or dispatches to a detected person, and
 * the aircraft leaves - a moving dot with no leg drawn is the one question the
 * air picture exists to answer, and no launch path selects the mission for them.
 * So off narrows the layer to `in_progress` rather than emptying it.
 */
const IN_PROGRESS_ONLY: maplibregl.FilterSpecification = ['==', ['get', 'status'], 'in_progress'];

export function setMissionLayerVisibility(map: maplibregl.Map, showPlanned: boolean) {
  for (const id of MISSION_LAYER_IDS) {
    if (!map.getLayer(id)) continue;
    // The layers stay VISIBLE either way; what changes is which features they
    // are allowed to draw. A layer hidden by `visibility` cannot make an
    // exception for the one route that matters.
    map.setLayoutProperty(id, 'visibility', 'visible');
    map.setFilter(id, showPlanned ? null : IN_PROGRESS_ONLY);
  }
}

/**
 * Range rings + the transit advisory leg.
 *
 * Registered EARLY (immediately after `sites`) so both sit at the bottom of the
 * layer stack: this is advice drawn under the picture, never over it. Nothing
 * here is toggled by visibility - `buildSourceUpdates` empties the sources when
 * the overlay is off or no airborne aircraft is picked, so "is it on screen" has
 * exactly one answer, in the data.
 *
 * Colour discipline: the rings are the neutral register the selection bracket
 * already owns - "the thing you picked" - at an opacity low enough that they
 * never compete with a live track but high enough to be seen at all
 * (RANGE_RING_INK), and the transit leg reuses the planner's amber. No new hue
 * enters the palette.
 */
function addRangeLayers(map: maplibregl.Map, mapStyle: MapStyleKey) {
  if (map.getSource('range-rings')) return;

  const ink = RANGE_RING_INK[mapStyle];
  const labelInk = RANGE_RING_LABEL_INK[mapStyle];

  const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

  map.addSource('range-rings', { type: 'geojson', data: empty });
  map.addLayer({
    id: 'range-ring-lines',
    type: 'line',
    source: 'range-rings',
    filter: ['==', ['geometry-type'], 'LineString'],
    // Below this the rings collapse into one smudge and carry no information.
    minzoom: RANGE_RING_MIN_ZOOM,
    paint: {
      'line-color': ink.color,
      'line-width': RANGE_RING_WIDTH_PX,
      'line-dasharray': [2, 4],
      'line-opacity': ink.opacity,
    },
  });
  map.addLayer({
    id: 'range-ring-labels',
    type: 'symbol',
    source: 'range-rings',
    filter: ['==', ['geometry-type'], 'Point'],
    minzoom: RANGE_RING_LABEL_MIN_ZOOM,
    layout: {
      'text-field': ['get', 'label'],
      'text-size': 9,
      'text-offset': [0, -0.6],
      // Refused rather than resolved: a ring caption that has shoved a callsign
      // out of the way has cost more than it is worth.
      'text-allow-overlap': false,
      'text-ignore-placement': false,
    },
    paint: {
      'text-color': labelInk.color,
      'text-halo-color': labelInk.halo,
      'text-halo-width': 1,
      'text-opacity': 0.75,
    },
  });

  map.addSource('transit-line', { type: 'geojson', data: empty });
  map.addLayer({
    id: 'transit-leg-line',
    type: 'line',
    source: 'transit-line',
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: {
      'line-color': '#F59E0B',
      'line-width': 1.5,
      'line-dasharray': [4, 3],
      'line-opacity': 0.85,
    },
  });
  map.addLayer({
    id: 'transit-leg-label',
    type: 'symbol',
    source: 'transit-line',
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'text-field': ['get', 'label'],
      'text-size': 10,
      'text-offset': [0, -0.9],
      // This one DOES win placement: it is the answer to the question the
      // operator just asked by hovering a mission row.
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#FCD34D',
      'text-halo-color': '#000000',
      'text-halo-width': 1.2,
    },
  });
}

/**
 * A coverage handover in progress (#251): a dotted line from the relief to the aircraft it is
 * relieving, and the time left until the station changes hands.
 *
 * COLOUR AND STROKE ARE ITS OWN. Cyan is the palette's coverage hue (`--status-cyan`), and the
 * stroke is DOTTED so it cannot be read as a mission route (dashed [6,3]), the amber transit leg
 * (dashed [4,3]) or a Class B outline (blue, dashed [2,2]) - the blue first used here was both the
 * grounded-aircraft colour and that outline (product review).
 *
 * ONE ABNORMAL LOOK (D17): a handover that needs attention turns the line red, takes the countdown
 * off, and rings each aircraft in red. The ring is what survives the last seconds, when the two are
 * stacked over one station and the line is a few pixels under two icons; at 20 px it clears the
 * icon it is drawn under.
 *
 * THE LINE IS LOW, THE LABEL IS HIGH. The line and ring are registered here, under the drone icons.
 * The label is lifted over every marker and callsign by `raiseSelectionBrackets`: in the last 15 s
 * the two aircraft are stacked over one station, which is exactly when a label under them is lost.
 * It sits ABOVE the pair, clear of the callsigns that hang below each icon.
 */
export const HANDOVER_LINK_COLOR = '#06B6D4';
const HANDOVER_LINK_LABEL_COLOR = '#4DC3DD';

function addHandoverLinkLayers(map: maplibregl.Map) {
  if (map.getSource('handover-link')) return;

  map.addSource('handover-link', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'handover-link-line',
    type: 'line',
    source: 'handover-link',
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': ['match', ['get', 'attention'], 'attention', STATUS_COLORS.critical, HANDOVER_LINK_COLOR],
      'line-width': 3,
      'line-dasharray': [0.1, 1.8],
      'line-opacity': 0.95,
    },
  });
  map.addLayer({
    id: 'handover-attention-ring',
    type: 'circle',
    source: 'handover-link',
    filter: ['==', ['get', 'kind'], 'attention-ring'],
    paint: {
      'circle-radius': 20,
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': STATUS_COLORS.critical,
      'circle-stroke-width': 2.5,
      'circle-stroke-opacity': 0.95,
    },
  });
  map.addLayer({
    id: HANDOVER_LINK_LABEL_LAYER_ID,
    type: 'symbol',
    source: 'handover-link',
    filter: ['==', ['get', 'kind'], 'label'],
    layout: {
      'text-field': ['get', 'label'],
      'text-size': 12,
      'text-anchor': 'bottom',
      'text-offset': [0, -1.4],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': HANDOVER_LINK_LABEL_COLOR,
      'text-halo-color': '#000000',
      'text-halo-width': 1.4,
    },
  });
}

function addAirspaceLayers(map: maplibregl.Map, visible: boolean) {
  if (map.getSource('airspace-classes')) return;

  try {
    if (!map.hasImage('airspace-tfr-hatch')) {
      map.addImage('airspace-tfr-hatch', makeTfrHatchImageData());
    }
  } catch {
    // Pattern optional - fall back to solid fill in layer paint
  }

  const vis = visible ? 'visible' : 'none';
  const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

  map.addSource('airspace-classes', { type: 'geojson', data: empty });
  map.addSource('airspace-tfrs', { type: 'geojson', data: empty });
  map.addSource('airspace-notams', { type: 'geojson', data: empty });

  map.addLayer({
    id: 'airspace-e-fill',
    type: 'fill',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'E'],
    paint: { 'fill-color': '#22C55E', 'fill-opacity': 0.08 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-b-fill',
    type: 'fill',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'B'],
    paint: { 'fill-color': '#2563EB', 'fill-opacity': 0.14 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-b-outline',
    type: 'line',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'B'],
    paint: { 'line-color': '#3B82F6', 'line-width': 1.5, 'line-dasharray': [2, 2], 'line-opacity': 0.85 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-c-fill',
    type: 'fill',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'C'],
    paint: { 'fill-color': '#A855F7', 'fill-opacity': 0.12 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-c-outline',
    type: 'line',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'C'],
    paint: { 'line-color': '#C084FC', 'line-width': 1.2, 'line-opacity': 0.7 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-d-fill',
    type: 'fill',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'D'],
    paint: { 'fill-color': '#3B82F6', 'fill-opacity': 0.1 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-d-outline',
    type: 'line',
    source: 'airspace-classes',
    filter: ['==', ['get', 'airspace_class'], 'D'],
    paint: { 'line-color': '#60A5FA', 'line-width': 1.2, 'line-dasharray': [3, 2], 'line-opacity': 0.85 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-class-labels',
    type: 'symbol',
    source: 'airspace-classes',
    filter: ['has', 'label'],
    layout: {
      visibility: vis,
      'text-field': ['get', 'label'],
      'text-size': 9,
      'text-allow-overlap': false,
      'text-max-width': 16,
    },
    paint: {
      'text-color': '#E4E4E7',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
      'text-opacity': 0.85,
    },
  });

  const tfrHatch = map.hasImage('airspace-tfr-hatch');
  map.addLayer({
    id: 'airspace-tfr-fill',
    type: 'fill',
    source: 'airspace-tfrs',
    paint: tfrHatch
      ? { 'fill-pattern': 'airspace-tfr-hatch', 'fill-opacity': 0.4 }
      : { 'fill-color': 'rgba(239, 68, 68, 0.22)' },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-tfr-outline',
    type: 'line',
    source: 'airspace-tfrs',
    paint: { 'line-color': '#EF4444', 'line-width': 1.5, 'line-opacity': 0.9 },
    layout: { visibility: vis },
  });
  map.addLayer({
    id: 'airspace-tfr-labels',
    type: 'symbol',
    source: 'airspace-tfrs',
    layout: {
      visibility: vis,
      'text-field': ['concat', 'TFR · ', ['coalesce', ['get', 'name'], 'Restriction']],
      'text-size': 9,
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#FCA5A5',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
    },
  });
  map.addLayer({
    id: 'airspace-notam-symbols',
    type: 'symbol',
    source: 'airspace-notams',
    layout: {
      visibility: vis,
      'text-field': '▲',
      'text-size': 14,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#FACC15',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
    },
  });
}

export function forceEnglishLabels(map: maplibregl.Map) {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const layer of style.layers) {
    if (layer.type !== 'symbol') continue;
    const tf = (layer.layout as Record<string, unknown> | undefined)?.['text-field'];
    if (!tf || !textFieldReferencesName(tf)) continue;
    try {
      map.setLayoutProperty(layer.id, 'text-field', ENGLISH_LABEL_EXPR);
    } catch {
      // Some layers may not accept expression updates
    }
  }
}

export function applyAltitudeLabelStyle(map: maplibregl.Map, showAltitudeLabels: boolean) {
  if (!map.getLayer('drone-labels')) return;
  const textField = showAltitudeLabels
    ? ['case', ['==', ['get', 'stale_level'], 2],
        ['concat', ['get', 'callsign'], ' · LOST'],
        ['concat', ['get', 'callsign'], ' · ', ['to-string', ['get', 'altitude_int']], 'm']]
    : ['case', ['==', ['get', 'stale_level'], 2],
        ['concat', ['get', 'callsign'], ' · LOST'],
        ['get', 'callsign']];
  map.setLayoutProperty('drone-labels', 'text-field', textField as maplibregl.ExpressionSpecification);
  const textColor = showAltitudeLabels
    ? ['case', ['==', ['get', 'stale_level'], 2], '#9CA3AF',
        ['match', ['get', 'altitude_band'],
          'low', '#3B82F6', 'mid', '#22C55E', 'high', '#F59E0B', 'extreme', '#EF4444', '#FFFFFF']]
    : ['case', ['==', ['get', 'stale_level'], 2], '#9CA3AF', '#FFFFFF'];
  map.setPaintProperty('drone-labels', 'text-color', textColor as maplibregl.ExpressionSpecification);
}

export interface AddMapLayersOptions {
  mapStyle: MapStyleKey;
  showAirspace: boolean;
  showMissions: boolean;
}

/**
 * The `mission-routes` / `mission-waypoints` sources and their layers.
 *
 * ONE definition of what a mission route looks like. The COP draws every route
 * in the org here; the mission record page and the printable briefing draw a
 * single one on their own static maps — and before this each had its own
 * hand-rolled line and marker, so the same mission was a dashed cyan line on
 * one screen and a solid blue one with white circles on another.
 *
 * Salience is data-driven: `buildMissionFeatures` stamps selected/hovered/dim
 * onto every feature (the same pattern the selection bracket uses), so a panel
 * hover or pick restyles routes through a plain `setData()`. A static map
 * simply never sets those flags and gets the resting register.
 *
 * `withHitLine` adds the invisible wide line that captures route clicks — the
 * COP needs it, a map nobody can click does not.
 */
export function addMissionRouteLayers(
  map: maplibregl.Map,
  { visible = true, withHitLine = false }: { visible?: boolean; withHitLine?: boolean } = {},
) {
  // Always visible, narrowed by filter - see `setMissionLayerVisibility`. `visible`
  // here means "planned routes too", not "on screen at all".
  const mVis = 'visible' as const;
  const mFilter = visible ? undefined : IN_PROGRESS_ONLY;
  const missionOpacity: maplibregl.ExpressionSpecification = [
    'case',
    ['boolean', ['get', 'selected'], false], 1,
    ['boolean', ['get', 'hovered'], false], 0.95,
    ['boolean', ['get', 'dim'], false], 0.3,
    0.8,
  ];
  map.addSource('mission-routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'mission-route-lines', type: 'line', source: 'mission-routes',
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'case',
        ['boolean', ['get', 'selected'], false], 3.5,
        ['boolean', ['get', 'hovered'], false], 3,
        2,
      ],
      'line-dasharray': [6, 3],
      'line-opacity': missionOpacity,
    },
    layout: { visibility: mVis },
    ...(mFilter ? { filter: mFilter } : {}),
  });
  if (withHitLine) {
    map.addLayer({
      id: 'mission-route-hit', type: 'line', source: 'mission-routes',
      paint: { 'line-color': '#000000', 'line-width': MISSION_ROUTE_HIT_WIDTH_PX, 'line-opacity': 0 },
      layout: { visibility: mVis },
      ...(mFilter ? { filter: mFilter } : {}),
    });
  }
  // 022/#700. THE CIRCLE AN ORBIT IS FLYING, on the live picture.
  //
  // An orbit stores one waypoint, so the route line above never fires for it and the waypoint
  // layer below drew a lone dot - the airspace the aircraft actually occupies was undrawn on a
  // display whose job is deconfliction. Added here, INSIDE `addMissionRouteLayers`, so it
  // inherits `mVis` and `mFilter`: turning mission routes off, or filtering by status, must take
  // the ring with them rather than leave a circle floating over a cleared picture.
  //
  // Under the waypoint layer so the centre dot and its number stay readable on top of the fill,
  // and dashed like the route lines it belongs with - the same "commanded, not yet flown" ink
  // the plan ring and the range rings use.
  map.addSource('mission-orbits', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'mission-orbit-fill', type: 'fill', source: 'mission-orbits',
    paint: {
      'fill-color': ['get', 'color'],
      // Deliberately fainter than the line: a 500 m disc at close zoom would otherwise wash out
      // everything drawn under it, which is most of the picture an operator is deconflicting.
      'fill-opacity': [
        'case',
        ['boolean', ['get', 'selected'], false], 0.1,
        ['boolean', ['get', 'dim'], false], 0.02,
        0.05,
      ],
    },
    layout: { visibility: mVis },
    ...(mFilter ? { filter: mFilter } : {}),
  });
  map.addLayer({
    id: 'mission-orbit-line', type: 'line', source: 'mission-orbits',
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'case',
        ['boolean', ['get', 'selected'], false], 3,
        ['boolean', ['get', 'hovered'], false], 2.5,
        1.75,
      ],
      'line-dasharray': [6, 3],
      'line-opacity': missionOpacity,
    },
    layout: { visibility: mVis },
    ...(mFilter ? { filter: mFilter } : {}),
  });
  map.addSource('mission-waypoints', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'mission-waypoint-circles', type: 'circle', source: 'mission-waypoints',
    paint: {
      'circle-radius': ['case', ['boolean', ['get', 'selected'], false], 5, 4],
      'circle-color': ['get', 'color'],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': '#FFFFFF',
      'circle-opacity': missionOpacity,
      'circle-stroke-opacity': missionOpacity,
    },
    layout: { visibility: mVis },
    ...(mFilter ? { filter: mFilter } : {}),
  });
  map.addLayer({
    id: 'mission-waypoint-labels', type: 'symbol', source: 'mission-waypoints',
    layout: { visibility: mVis, 'text-field': ['to-string', ['get', 'sequence_order']], 'text-size': 9, 'text-allow-overlap': true, 'text-offset': [0, -1.5] },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
      'text-opacity': missionOpacity,
    },
    ...(mFilter ? { filter: mFilter } : {}),
  });
}

export function addMapDataLayers(map: maplibregl.Map, options: AddMapLayersOptions) {
  const { mapStyle, showAirspace, showMissions } = options;

  // Both bracket layers share this one image, so register it once here rather
  // than in each source block. Guarded on a source being absent because that is
  // exactly when a bracket layer is about to be (re)created - after setStyle()
  // wipes the style, or on first load. addMapDataLayers itself runs twice per
  // cold load ('style.load' then 'load'), and re-registering on that second,
  // no-op pass would force a needless symbol re-layout of every drone and dock
  // tile. Same discipline as ensureDockGlyphs, which sits inside its own guard.
  if (!map.getSource('docks') || !map.getSource('drones')) {
    ensureSelectionBracketImage(map);
  }

  if (!map.getSource('sites')) {
    map.addSource('sites', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'site-circles', type: 'circle', source: 'sites', paint: { 'circle-radius': 6, 'circle-color': 'transparent', 'circle-stroke-width': 2, 'circle-stroke-color': '#3B82F6', 'circle-opacity': 0.8 } });
    map.addLayer({ id: 'site-labels', type: 'symbol', source: 'sites', layout: { 'text-field': ['get', 'name'], 'text-size': 9, 'text-offset': [0, 1.8] }, paint: { 'text-color': '#3B82F6', 'text-halo-color': '#000', 'text-halo-width': 1, 'text-opacity': 0.7 } });
  }

  // Before every other data layer: range rings and the transit leg are advice
  // drawn UNDER the picture they are advising about.
  addRangeLayers(map, mapStyle);
  // Same place, same reason: the handover line joins two drone icons and must never cover one.
  addHandoverLinkLayers(map);

  // Safe land zones BEFORE the docks, so a dock glyph always draws over its
  // own fallback marker when the two are close. Amber: it is where the aircraft
  // goes when the tag landing has failed, so it must read as a caution, not as
  // a second dock.
  if (!map.getSource('dock-safe-land')) {
    map.addSource('dock-safe-land', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'dock-safe-land-rings',
      type: 'circle',
      source: 'dock-safe-land',
      paint: {
        'circle-radius': 9,
        'circle-color': '#F59E0B',
        'circle-opacity': 0.15,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#F59E0B',
        'circle-stroke-opacity': 0.9,
      },
    });
    map.addLayer({
      id: 'dock-safe-land-labels',
      type: 'symbol',
      source: 'dock-safe-land',
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 9,
        'text-offset': [0, 1.6],
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#F59E0B', 'text-halo-color': '#000000', 'text-halo-width': 1, 'text-opacity': 0.9 },
    });
  }

  if (!map.getSource('docks')) {
    map.addSource('docks', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

    // Distinct FlytMini glyph (square, status-coloured) so a dock is never
    // mistaken for a drone. Four synchronous canvas variants are registered
    // before the layer so there is no async image load to race the layer add.
    ensureDockGlyphs(map);
    map.addLayer({
      id: 'dock-symbols',
      type: 'symbol',
      source: 'docks',
      layout: {
        'icon-image': [
          'match', ['get', 'symbol_state'],
          'ready', 'dock-ready',
          'busy', 'dock-busy',
          'offline', 'dock-offline',
          'emergency', 'dock-emergency',
          'dock-busy',
        ],
        // Emergency docks a touch larger to draw the eye.
        'icon-size': ['case', ['==', ['get', 'symbol_state'], 'emergency'], 1.25, 1.05],
        'icon-allow-overlap': true,
      },
    });
    map.addLayer({
      id: 'dock-labels',
      type: 'symbol',
      source: 'docks',
      layout: {
        'text-field': ['get', 'callsign'],
        'text-size': 9,
        'text-offset': selectedTextOffset(2.2, 9),
        'text-allow-overlap': true,
      },
      paint: { 'text-color': ['get', 'color'], 'text-halo-color': '#000000', 'text-halo-width': 1, 'text-opacity': 0.85 },
    });
    // Invisible, generous click target sitting on top of the visible dock
    // marker. The dock click handler binds to THIS layer (stable id), so the
    // visible marker can change freely without touching the click wiring.
    // A transparent circle is still returned by queryRenderedFeatures / fires
    // layer click events - opacity 0 is hit-testable (only visibility:none is not).
    map.addLayer({
      id: 'dock-hit',
      type: 'circle',
      source: 'docks',
      paint: { 'circle-radius': DOCK_HIT_RADIUS_PX, 'circle-color': '#000000', 'circle-opacity': 0 },
    });
    // Added after dock-symbols so the bracket draws over the glyph, never under it.
    map.addLayer({
      id: 'dock-selection-bracket',
      type: 'symbol',
      source: 'docks',
      filter: SELECTION_BRACKET_FILTER,
      layout: SELECTION_BRACKET_LAYOUT,
    });
  }

  // External triggers - an unanswered person detection the operator can dispatch
  // to. Added AFTER docks on purpose: a detection is almost always at a dock, and
  // an alerting point that draws underneath the dock glyph is a point nobody sees.
  if (!map.getSource('triggers')) {
    map.addSource('triggers', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'trigger-rings',
      type: 'circle',
      source: 'triggers',
      paint: {
        'circle-radius': 11,
        // Red: this is the one thing on the picture asking the operator to act.
        'circle-color': '#EF4444',
        'circle-opacity': 0.2,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#EF4444',
        'circle-stroke-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'trigger-labels',
      type: 'symbol',
      source: 'triggers',
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-offset': [0, 1.8],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#EF4444',
        'text-halo-color': '#000000',
        'text-halo-width': 1,
        'text-opacity': 0.95,
      },
    });
    // Same reasoning as `dock-hit`: an invisible, generous target the click
    // handler binds to, so the visible glyph can change without touching the
    // click wiring. Opacity 0 is still hit-testable; visibility:none is not.
    map.addLayer({
      id: 'trigger-hit',
      type: 'circle',
      source: 'triggers',
      paint: { 'circle-radius': TRIGGER_HIT_RADIUS_PX, 'circle-color': '#000000', 'circle-opacity': 0 },
    });
  }

  addAirspaceLayers(map, showAirspace);

  if (!map.getSource('geofences')) {
    map.addSource('geofences', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'geofence-fill', type: 'fill', source: 'geofences', paint: { 'fill-color': ['get', 'color'], 'fill-opacity': TRACK_INK[mapStyle].geofenceFill } });
    map.addLayer({ id: 'geofence-border', type: 'line', source: 'geofences', filter: ['!=', ['get', 'type'], 'jamming'], paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-dasharray': [4, 2] } });
    map.addLayer({ id: 'geofence-border-jamming', type: 'line', source: 'geofences', filter: ['==', ['get', 'type'], 'jamming'], paint: { 'line-color': '#EF4444', 'line-width': 3, 'line-dasharray': [6, 3, 2, 3], 'line-opacity': 0.9 } });
    // Zone NAME and TYPE, on the map itself.
    //
    // Until the operator could choose a zone's colour, colour WAS the type —
    // red restricted, green operational, amber warning — and that was the only
    // channel carrying it: there is no popup, no click handler on the fill, and
    // nothing else here draws a zone's name. Letting an operator paint a
    // restricted no-fly green would have made it pixel-identical to an
    // operational keep-in fence, which `mission-planning.ts` treats as a
    // boundary to stay INSIDE. Same geometry, opposite instruction.
    //
    // A label restores type independently of whatever colour is chosen, and
    // also separates a drawn zone from the regulatory airspace underneath it —
    // Class C/D polygons carry their own labels and these now do too.
    map.addLayer({
      id: 'geofence-label',
      type: 'symbol',
      source: 'geofences',
      layout: {
        'text-field': [
          'concat',
          ['upcase', ['coalesce', ['get', 'name'], 'ZONE']],
          '  ',
          // Abbreviated, because this sits on top of the operator's map: the
          // full word crowds a small zone at the zoom levels the COP flies at.
          ['match', ['get', 'type'],
            'restricted', 'RSTR',
            'operational', 'OPS',
            'warning', 'WARN',
            'jamming', 'JAM',
            ['upcase', ['get', 'type']]],
        ],
        'text-size': 9,
        // Overlap is refused rather than resolved: two zone labels stacked on
        // each other is worse than one, and the operator can zoom.
        'text-allow-overlap': false,
        'text-max-width': 14,
      },
      paint: {
        'text-color': '#E4E4E7',
        'text-halo-color': '#000000',
        'text-halo-width': 1,
        'text-opacity': 0.9,
      },
    });
  }

  if (!map.getSource('mission-routes')) {
    addMissionRouteLayers(map, { visible: showMissions, withHitLine: true });
  }

  if (!map.getSource('plan-route')) {
    map.addSource('plan-route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'plan-route-line', type: 'line', source: 'plan-route',
      paint: { 'line-color': '#F59E0B', 'line-width': 2.5, 'line-opacity': 0.9 },
    });
    // Live progress: the currently-active leg drawn brighter on top of the plan
    // polyline. A dash pattern reads as "in transit" and animates via the
    // line-dashoffset ticker (startPlanActiveLegAnimation).
    map.addSource('plan-active-leg', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'plan-active-leg-line', type: 'line', source: 'plan-active-leg',
      paint: {
        'line-color': '#22C55E',
        'line-width': 4,
        'line-opacity': 0.95,
        'line-dasharray': [0, 2, 3],
      },
    });
    map.addSource('plan-waypoints', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'plan-home-circle', type: 'circle', source: 'plan-waypoints',
      filter: ['==', ['get', 'kind'], 'home'],
      paint: { 'circle-radius': 7, 'circle-color': '#3B82F6', 'circle-stroke-width': 2, 'circle-stroke-color': '#FFFFFF', 'circle-opacity': 0.9 },
    });
    map.addLayer({
      id: 'plan-waypoint-circles', type: 'circle', source: 'plan-waypoints',
      filter: ['==', ['get', 'kind'], 'wp'],
      paint: {
        // Active target is largest, then selected, then plain.
        'circle-radius': ['case', ['get', 'active'], 11, ['case', ['get', 'selected'], 10, 8]],
        // Colour priority: invalid (red) > active target (green) > reached (dim gray) > pending (amber).
        'circle-color': [
          'case',
          ['get', 'invalid'], '#EF4444',
          ['get', 'active'], '#22C55E',
          ['get', 'reached'], '#52525B',
          '#F59E0B',
        ],
        'circle-stroke-width': ['case', ['get', 'active'], 3, 2],
        'circle-stroke-color': [
          'case',
          ['get', 'active'], '#FFFFFF',
          ['get', 'selected'], '#FFFFFF',
          '#000000',
        ],
        'circle-opacity': ['case', ['get', 'reached'], 0.7, 0.95],
      },
    });
    map.addLayer({
      id: 'plan-waypoint-labels', type: 'symbol', source: 'plan-waypoints',
      layout: { 'text-field': ['get', 'label'], 'text-size': 10, 'text-allow-overlap': true },
      paint: { 'text-color': ['case', ['==', ['get', 'kind'], 'home'], '#FFFFFF', ['case', ['get', 'invalid'], '#FFFFFF', ['case', ['get', 'reached'], '#E4E4E7', '#000000']]] as unknown as maplibregl.ExpressionSpecification },
    });
    // Invisible, generous grab target over the draft route's waypoints. Added
    // LAST of the plan layers so it wins hit-testing against the visible dot
    // and its label, and it stays BELOW the controller/traffic/drone layers
    // registered after this block — an aircraft parked on a waypoint still
    // takes the click. `circle-opacity: 0` is hit-testable and is still
    // returned by queryRenderedFeatures; only `visibility: none` is not.
    map.addLayer({
      id: 'plan-waypoint-hit', type: 'circle', source: 'plan-waypoints',
      filter: ['==', ['get', 'kind'], 'wp'],
      paint: { 'circle-radius': PLAN_WAYPOINT_HIT_RADIUS_PX, 'circle-color': '#000000', 'circle-opacity': 0 },
    });
  }

  if (!map.getSource('zone-draw-preview')) {
    // Painted from the feature's own `color`, exactly as the committed
    // geofence-fill/geofence-border layers are: the operator picks a colour
    // while drawing and the preview must already be wearing it, so switching
    // colour is just the next setData() rather than a layer restyle.
    map.addSource('zone-draw-preview', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'zone-draw-fill', type: 'fill', source: 'zone-draw-preview', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.15 } });
    map.addLayer({ id: 'zone-draw-line', type: 'line', source: 'zone-draw-preview', paint: { 'line-color': ['get', 'color'], 'line-width': 2 } });
    // The white stroke stays: the vertex dot is now whatever the operator
    // picked, and the pale swatches (Amber, Slate) against a 0.15 fill of
    // themselves have almost nothing to separate them from the shape.
    map.addLayer({ id: 'zone-draw-verts', type: 'circle', source: 'zone-draw-preview', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': 5, 'circle-color': ['get', 'color'], 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
  }

  if (!map.getSource('plan-orbit')) {
    // 022. The circle an ORBIT mission will fly, around the single point the operator placed.
    // Added AFTER the plan-route layers so the ring sits over the route rather than under it,
    // and dashed rather than solid: it is the commanded path, but nothing has flown it yet,
    // which is the same distinction the range rings draw.
    map.addSource('plan-orbit', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'plan-orbit-fill',
      type: 'fill',
      source: 'plan-orbit',
      paint: { 'fill-color': '#38BDF8', 'fill-opacity': 0.08 },
    });
    map.addLayer({
      id: 'plan-orbit-line',
      type: 'line',
      source: 'plan-orbit',
      paint: { 'line-color': '#38BDF8', 'line-width': 2, 'line-dasharray': [3, 2] },
    });
  }

  if (!map.getSource('drone-auto-trails')) {
    map.addSource('drone-auto-trails', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'auto-trail-lines', type: 'line', source: 'drone-auto-trails', paint: { 'line-color': ['get', 'color'], 'line-width': 1.5 * TRACK_INK[mapStyle].width, 'line-opacity': TRACK_INK[mapStyle].autoTrail } });
  }

  if (!map.getSource('trails')) {
    map.addSource('trails', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'trail-lines', type: 'line', source: 'trails', paint: { 'line-color': ['get', 'color'], 'line-width': 2 * TRACK_INK[mapStyle].width, 'line-opacity': TRACK_INK[mapStyle].trail } });
  }

  if (!map.getSource('controller-link')) {
    map.addSource('controller-link', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'controller-link-line', type: 'line', source: 'controller-link', paint: { 'line-color': mapStyle === 'satellite' ? '#FFFFFF' : '#A1A1AA', 'line-width': 1.5 * TRACK_INK[mapStyle].width, 'line-dasharray': [4, 4], 'line-opacity': TRACK_INK[mapStyle].link } });
  }

  if (!map.getSource('controllers')) {
    map.addSource('controllers', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

    // Dark ink ONLY on the street map, which is the one pale ground. This was a
    // two-way branch on a three-value key, so imagery - now the default - took
    // the `else` and got the glyph drawn for CARTO's near-white land fill, on
    // top of the operator's own position at takeoff.
    const rcIconSrc = mapStyle === 'bright' ? '/rc-icon-dark.svg' : '/rc-icon.svg';
    const rcImg = new Image(28, 24);
    rcImg.onload = () => {
      if (map.hasImage('rc-icon')) map.removeImage('rc-icon');
      map.addImage('rc-icon', rcImg);
      if (!map.getLayer('controller-symbols')) {
        map.addLayer({
          id: 'controller-symbols', type: 'symbol', source: 'controllers',
          layout: { 'icon-image': 'rc-icon', 'icon-size': 0.9, 'icon-allow-overlap': true },
          paint: { 'icon-opacity': 0.8 },
        });
      }
      if (map.getLayer('controller-circles')) map.setLayoutProperty('controller-circles', 'visibility', 'none');
      // This callback appends controller-symbols to the top of the stack, and
      // it can resolve after the drone icon's. Re-assert so the RC glyph - which
      // sits at the operator's own GPS fix, i.e. on top of a drone at takeoff -
      // never lands above the selection bracket.
      raiseSelectionBrackets(map);
    };
    rcImg.crossOrigin = 'anonymous';
    rcImg.src = rcIconSrc;

    map.addLayer({ id: 'controller-circles', type: 'circle', source: 'controllers', paint: { 'circle-radius': 5, 'circle-color': '#A1A1AA', 'circle-stroke-width': 1.5, 'circle-stroke-color': '#000', 'circle-opacity': 0.7 } });
  }

  if (!map.getSource('traffic')) {
    map.addSource('traffic', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

    const diamondImg = new Image(24, 24);
    diamondImg.onload = () => {
      if (map.hasImage('traffic-diamond-icon')) map.removeImage('traffic-diamond-icon');
      map.addImage('traffic-diamond-icon', diamondImg, { sdf: true });
      if (!map.getLayer('traffic-symbols')) {
        map.addLayer({
          id: 'traffic-symbols', type: 'symbol', source: 'traffic',
          layout: {
            'icon-image': 'traffic-diamond-icon',
            'icon-size': 0.9,
            'icon-rotate': ['get', 'heading'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
          },
          paint: { 'icon-color': '#A855F7', 'icon-opacity': 0.75 },
        });
      }
      if (map.getLayer('traffic-circles')) map.setLayoutProperty('traffic-circles', 'visibility', 'none');
      raiseSelectionBrackets(map);
    };
    diamondImg.crossOrigin = 'anonymous';
    diamondImg.src = '/icon-traffic-diamond.svg';

    map.addLayer({ id: 'traffic-circles', type: 'circle', source: 'traffic', paint: { 'circle-radius': 4, 'circle-color': '#A855F7', 'circle-stroke-width': 1, 'circle-stroke-color': '#000', 'circle-opacity': 0.6 } });
    map.addLayer({ id: 'traffic-labels', type: 'symbol', source: 'traffic', layout: { 'text-field': ['get', 'callsign'], 'text-size': 8, 'text-offset': [0, 1.5] }, paint: { 'text-color': '#A855F7', 'text-halo-color': '#000', 'text-halo-width': 1, 'text-opacity': 0.7 } });
  }

  if (!map.getSource('drone-heading-lines')) {
    map.addSource('drone-heading-lines', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: 'heading-line-layer', type: 'line', source: 'drone-heading-lines', paint: { 'line-color': ['get', 'color'], 'line-width': 1.5, 'line-opacity': 0.5 } });
  }

  if (!map.getSource('drones')) {
    map.addSource('drones', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

    const img = new Image(32, 32);
    img.onload = () => {
      if (!map.hasImage('drone-icon')) map.addImage('drone-icon', img, { sdf: true });
      if (!map.getLayer('drone-symbols')) {
        map.addLayer({
          id: 'drone-symbols', type: 'symbol', source: 'drones',
          layout: {
            'icon-image': 'drone-icon',
            'icon-size': ['case', ['==', ['get', 'status'], 'flying'], 0.9, 0.7],
            'icon-rotate': ['get', 'heading'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
          },
          paint: { 'icon-color': ['get', 'color'], 'icon-opacity': 0.95 },
        });
      }
      if (!map.getLayer('drone-labels')) {
        map.addLayer({
          id: 'drone-labels', type: 'symbol', source: 'drones',
          layout: {
            'text-field': ['case',
              ['==', ['get', 'stale_level'], 2],
              ['concat', ['get', 'callsign'], ' · LOST'],
              ['get', 'callsign'],
            ],
            'text-size': 10,
            'text-offset': selectedTextOffset(2.2, 10),
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': ['case',
              ['==', ['get', 'stale_level'], 2], '#9CA3AF',
              '#FFFFFF',
            ] as unknown as maplibregl.ExpressionSpecification,
            'text-halo-color': '#000000', 'text-halo-width': 1,
          },
        });
      }
      if (map.getLayer('drone-circles')) {
        map.setLayoutProperty('drone-circles', 'visibility', 'none');
      }
      // drone-symbols/drone-labels were just appended above the brackets, which
      // were added synchronously so they survive this image failing to load at
      // all. Re-assert bracket order so a marker cannot clip their corners.
      raiseSelectionBrackets(map);
    };
    img.crossOrigin = 'anonymous';
    img.src = '/drone-icon.svg';

    map.addLayer({ id: 'drone-circles', type: 'circle', source: 'drones', paint: { 'circle-radius': ['case', ['==', ['get', 'status'], 'flying'], 7, 5], 'circle-color': ['get', 'color'], 'circle-stroke-width': 2, 'circle-stroke-color': '#000', 'circle-opacity': 0.9 } });

    // MANUAL AUTHORITY, on the track itself.
    //
    // `manual_control_state` is broadcast org-wide so a second console can learn
    // that an aircraft is already being flown by hand — but until this the only
    // thing that read it was the refusal on the MANUAL key of the SELECTED
    // aircraft. Operator B scanning the picture saw an ordinary track and could
    // send RTH to an aircraft operator A was threading between buildings.
    //
    // Two marks, for two reading distances: the ring is what the operator
    // catches while scanning, the tag is what they read once something caught
    // them. Both are added here rather than in img.onload, so a blocked
    // /drone-icon.svg cannot take the safety mark down with it — the same
    // reason the selection bracket is added synchronously below.
    map.addLayer({
      id: 'drone-manual-ring',
      type: 'circle',
      source: 'drones',
      filter: MANUAL_HOLD_FILTER,
      paint: {
        // Outside the 29 px aircraft glyph, inside the 46 px selection bracket,
        // so a held aircraft that is also selected shows both marks whole.
        'circle-radius': ['case', ['==', ['get', 'status'], 'flying'], 15, 13],
        'circle-color': MANUAL_HOLD_COLOR,
        // Hollow. A filled disc would sit on the symbol the operator is trying
        // to read heading and status off.
        'circle-opacity': 0,
        'circle-stroke-width': 2,
        'circle-stroke-color': MANUAL_HOLD_COLOR,
        'circle-stroke-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'drone-manual-tag',
      type: 'symbol',
      source: 'drones',
      filter: MANUAL_HOLD_FILTER,
      layout: {
        // `MANUAL · <holder>`, or `MANUAL` alone when the broadcast carried no
        // name — a hold whose holder we cannot name is still a hold.
        'text-field': ['get', 'manual_label'],
        'text-size': 9,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.05,
        // Wrap a long account name onto a second line rather than letting it
        // run across the picture. Anchored at its bottom edge, so it grows
        // upward, away from the aircraft.
        'text-max-width': 12,
        'text-anchor': 'bottom',
        // ABOVE the marker: the callsign owns the space below it.
        'text-offset': selectedTextOffset(2.6, 9, 'above'),
        // Never dropped by the placement engine. A mark that vanishes because
        // two aircraft drifted close together is worse than no mark at all —
        // the operator learns to trust its absence.
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': MANUAL_HOLD_COLOR,
        'text-halo-color': '#000000',
        'text-halo-width': 1,
      },
    });

    // Added here, not in img.onload, so a selected drone still gets its bracket
    // when /drone-icon.svg fails to load and only drone-circles renders.
    map.addLayer({
      id: 'drone-selection-bracket',
      type: 'symbol',
      source: 'drones',
      filter: SELECTION_BRACKET_FILTER,
      layout: SELECTION_BRACKET_LAYOUT,
    });
  }

  // Synchronous final pass. The dock bracket is added early (docks are the
  // second source registered) and would otherwise sit under ~24 later layers -
  // including plan-route-line, which by construction runs from the selected
  // dock outward, and the drone layers for an aircraft parked on its pad.
  raiseSelectionBrackets(map);
}

/**
 * Animate the active-leg dash so the current leg reads as "in transit".
 * Cycles line-dasharray via requestAnimationFrame. Returns a stop function that
 * cancels the loop - call it on cleanup. No-op if the layer is absent.
 */
export function startPlanActiveLegAnimation(map: maplibregl.Map): () => void {
  const DASH_SEQUENCE: number[][] = [
    [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5],
    [2, 4, 1], [2.5, 4, 0.5], [3, 4, 0], [0, 0.5, 3, 3.5],
    [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2], [0, 2.5, 3, 1.5],
    [0, 3, 3, 1], [0, 3.5, 3, 0.5],
  ];
  let step = 0;
  let raf = 0;
  let lastAt = 0;
  const STEP_MS = 80;
  const tick = (t: number) => {
    if (t - lastAt >= STEP_MS) {
      lastAt = t;
      step = (step + 1) % DASH_SEQUENCE.length;
      if (map.getLayer('plan-active-leg-line')) {
        try {
          map.setPaintProperty('plan-active-leg-line', 'line-dasharray', DASH_SEQUENCE[step]);
        } catch {
          // Layer torn down mid-frame - ignore.
        }
      }
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

export function setMapInteractionCursor(map: maplibregl.Map | null, mode: MapMode) {
  const canvas = map?.getCanvas();
  if (!canvas) return;
  canvas.style.cursor = mapModeWantsCrosshair(mode) ? 'crosshair' : '';
}
